import { useMemo } from "react"
import { gql } from "~/__generated__"
import { AppHexColor, CachedViewerContextFragment } from "~/__generated__/graphql"
import { AppText } from "~/ui/app-text"
import { cn } from "./cn"
import imageResize from "./image-resize"

const sizes = {
  menu: {
    width: 40,
    height: 40,
  },
} as const

export type AvatarSize = keyof typeof sizes

const fontSizes = {
  menu: "text-15",
}

type BaseProps = {
  size: keyof typeof sizes
  onPress?: () => void
}

export const UserAvatarWithFallback = ({
  user,
  ...rest
}: BaseProps & {
  user: CachedViewerContextFragment
}) => <AvatarWithFallback url={user.avatarUrl} name={user.displayOrUsername} {...rest} />

export type AvatarWithFallbackProps = BaseProps & {
  url: string | undefined | null
  name: string
}

export const AvatarWithFallback = ({ url, name, size, onPress }: AvatarWithFallbackProps) => {
  const sizeStyle = sizes[size]
  const fallbackColors = useMemo(() => {
    return Object.values(bgColorMap)[keyFromString(name)]
  }, [name])

  const avatar = useMemo(() => {
    const sharedStyle = {
      ...sizeStyle,
    }
    if (!url) {
      return (
        <div className={cn(sharedStyle, fallbackColors)}>
          <AppText className={cn("opacity-70", fontSizes[size], fallbackColors)}>
            {name.charAt(0) || ""}
          </AppText>
        </div>
      )
    } else {
      const resizedUrl = imageResize(url, { ...sizeStyle, fit: "crop" })
      return (
        <div style={sharedStyle}>
          <img src={resizedUrl} style={sharedStyle} />
        </div>
      )
    }
  }, [fallbackColors, name, sizeStyle, size, url])

  return (
    <button disabled={!onPress} onClick={onPress} className="flex overflow-hidden rounded-full">
      {avatar}
    </button>
  )
}

gql(/* GraphQL */ `
  fragment User_Avatar on User {
    id
    avatarUrl
    displayOrUsername
  }
`)

const bgColorMap: Record<AppHexColor, string> = {
  [AppHexColor.Blue]: "bg-support-blue text-base-white",
  [AppHexColor.Clay]: "bg-support-clay text-base-black",
  [AppHexColor.Cream]: "bg-support-cream text-base-black",
  [AppHexColor.Green]: "bg-support-green text-base-black",
  [AppHexColor.Lime]: "bg-support-lime text-base-black",
  [AppHexColor.Magenta]: "bg-support-magenta text-base-black",
  [AppHexColor.Midnight]: "bg-support-midnight text-base-black",
  [AppHexColor.Moss]: "bg-support-moss text-base-black",
  [AppHexColor.Orange]: "bg-support-orange text-base-black",
  [AppHexColor.Pink]: "bg-support-pink text-base-black",
  [AppHexColor.Purple]: "bg-support-purple text-white",
  [AppHexColor.Red]: "bg-support-red text-base-black",
  [AppHexColor.Sky]: "bg-support-sky text-base-black",
  [AppHexColor.SoftLime]: "bg-support-soft-lime text-base-black",
  [AppHexColor.Yellow]: "bg-support-yellow text-base-black",
}

const keyFromString = (input: string): number => {
  let hash = 0

  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i)
    hash &= hash // Convert to 32bit integer
  }

  const min = 0
  const max = 14
  const range = max - min + 1
  const randomNumber = (((hash % range) + range) % range) + min

  return randomNumber
}
